import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, FileText, Terminal } from "lucide-react";
import usePageview from "@/lib/usePageview";

/**
 * Phase 25.13h — Terms of Service.
 *
 * AU-aligned SaaS terms drafted as a baseline. Designed to satisfy the prerequisites for:
 *   · Square Production app review
 *   · Stripe live payments
 *   · Investor due diligence
 *
 * For high-risk customers, a lawyer should review before relying on this in
 * regulated industries (banking, healthcare, government tenders).
 */

const EFFECTIVE_DATE = "1 May 2026";
const ENTITY = "Dara Technology Pty Ltd (ACN to be confirmed)";
const CONTACT_EMAIL = "legal@daraos.ai";
const SUPPORT_EMAIL = "support@daraos.ai";

const SECTIONS = [
  {
    id: "01",
    title: "Acceptance of terms",
    body: (
      <>
        These Terms of Service (&quot;<strong>Terms</strong>&quot;) form a binding contract
        between you and {ENTITY} (&quot;<strong>DARA</strong>&quot;, &quot;<strong>we</strong>
        &quot;, &quot;<strong>us</strong>&quot;) governing your use of the DARA OS
        platform, the daraos.ai website, and any related services
        (collectively, the &quot;<strong>Service</strong>&quot;). By creating an account,
        clicking &quot;I accept,&quot; or using the Service in any way, you agree to be
        bound by these Terms.
      </>
    ),
  },
  {
    id: "02",
    title: "Eligibility",
    body: (
      <>
        You must be at least 18 years of age and have authority to bind the
        business on whose behalf you sign up. The Service is B2B and is not
        intended for personal, household, or consumer use. If you are signing
        up on behalf of an entity, you represent that you have the authority
        to bind that entity to these Terms.
      </>
    ),
  },
  {
    id: "03",
    title: "Subscription, trial, and billing",
    body: (
      <>
        <p className="mb-4">
          DARA is offered on a paid subscription basis with the following tiers
          (USD, billed monthly):
        </p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li><strong>Starter</strong> — $299/month · single-site operators</li>
          <li><strong>Scale</strong> — $1,499/month · multi-site mid-market</li>
          <li><strong>Professional</strong> — $3,499/month · treasury, commodities, multi-jurisdiction</li>
          <li><strong>Enterprise</strong> — custom pricing · regulated and air-gapped</li>
        </ul>
        <p className="mt-4 mb-4">
          We offer a free 14-day trial of the Starter and Scale tiers, no credit
          card required to start. At trial expiry your subscription will only
          commence if you have provided a payment method and clicked
          &quot;Confirm subscription&quot;. We do not auto-charge trial
          accounts that have not provided payment details.
        </p>
        <p className="mb-4">
          For active paid subscriptions, charges are processed monthly in advance
          via Stripe. Sales tax (GST or equivalent) is applied where required.
          Refunds for partial months are not provided as a standard practice;
          however, we may offer a pro-rata refund at our discretion if the
          Service has been materially unavailable.
        </p>
        <p>
          We may change our pricing on at least 30 days&apos; written notice.
          Customers on the Founding Customer programme have their original
          locked rate honoured for the lifetime of their continuous subscription.
        </p>
      </>
    ),
  },
  {
    id: "04",
    title: "Your data, our platform",
    body: (
      <>
        <p className="mb-4">
          You retain all rights, title, and interest in and to the data you
          upload, paste, or sync into your DARA workspace (&quot;
          <strong>Customer Data</strong>&quot;). We claim no ownership over
          Customer Data and will not sell, license, or otherwise commercialise
          it beyond providing the Service you have requested.
        </p>
        <p className="mb-4">
          You grant DARA a limited, non-exclusive, royalty-free licence to access,
          process, transmit, and display Customer Data solely for the purpose of
          operating the Service for you. This licence terminates automatically
          when you delete the data or cancel your subscription.
        </p>
        <p>
          DARA retains all rights, title, and interest in and to the Service
          itself — including its code, design, models, integrations,
          jurisdictional rulebooks, and AI-generated outputs that are not
          derivatives of your specific Customer Data.
        </p>
      </>
    ),
  },
  {
    id: "05",
    title: "AI-generated outputs",
    body: (
      <>
        <p className="mb-4">
          The Service uses large language models (currently Anthropic&apos;s
          Claude 4.5 Sonnet) to synthesise Customer Data into briefings,
          recommendations, and analytical outputs. You acknowledge that:
        </p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li>AI-generated outputs are advisory and should be reviewed by a human before being acted upon for material business or financial decisions.</li>
          <li>AI outputs can occasionally contain errors, hallucinations, or omissions, especially where Customer Data is incomplete or anomalous.</li>
          <li>DARA does not warrant the accuracy, completeness, or fitness-for-purpose of AI-generated outputs for any specific use case.</li>
          <li>You remain solely responsible for decisions made based on AI-generated outputs.</li>
        </ul>
      </>
    ),
  },
  {
    id: "06",
    title: "Acceptable use",
    body: (
      <>
        <p className="mb-4">You agree not to use the Service to:</p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li>Upload, store, or transmit content that is illegal, infringing, defamatory, fraudulent, or harmful</li>
          <li>Attempt to reverse-engineer, decompile, or otherwise extract the source code of the Service</li>
          <li>Circumvent rate limits, scraping protections, or access controls</li>
          <li>Use the Service to develop a competing product</li>
          <li>Use the Service in violation of any applicable export control, sanctions, or anti-money-laundering laws</li>
          <li>Submit data containing protected health information (PHI), defence-classified information, or unredacted full credit card numbers, unless you have signed a custom Enterprise agreement with us permitting such data</li>
        </ul>
      </>
    ),
  },
  {
    id: "07",
    title: "Third-party integrations",
    body: (
      <>
        You may connect third-party services (e.g. Square, Xero, MYOB, Stripe)
        to your workspace. Each of these services operates under its own terms
        and privacy policies, which you should review. DARA is not responsible
        for the availability, accuracy, or behaviour of third-party services,
        and DARA is not liable for any losses caused by a third-party service&apos;s
        failure, downtime, or change in API behaviour.
      </>
    ),
  },
  {
    id: "08",
    title: "Service availability",
    body: (
      <>
        We target 99.5% monthly uptime for the production Service, excluding
        scheduled maintenance announced at least 48 hours in advance. We do not
        currently provide a contractual service-level agreement (SLA) for tiers
        below Enterprise. Enterprise customers may negotiate a written SLA as
        part of their custom agreement.
      </>
    ),
  },
  {
    id: "09",
    title: "Account suspension and termination",
    body: (
      <>
        <p className="mb-4">
          We may suspend or terminate your account, with or without notice, if:
        </p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li>You breach these Terms or our Acceptable Use Policy</li>
          <li>Your account is the subject of fraud, chargeback, or abuse</li>
          <li>You fail to pay an outstanding invoice within 14 days of its due date</li>
          <li>We are required to do so by law or by direction of a competent regulator</li>
        </ul>
        <p className="mt-4">
          You may cancel your subscription at any time through Settings →
          Billing, or by emailing{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#FFB000] underline">{SUPPORT_EMAIL}</a>.
          Upon cancellation, you have 30 days to export your data before it is
          permanently deleted.
        </p>
      </>
    ),
  },
  {
    id: "10",
    title: "Warranties and disclaimers",
    body: (
      <>
        <p className="mb-4">
          THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot;
          To the maximum extent permitted by law, DARA disclaims all warranties,
          express or implied, including warranties of merchantability, fitness
          for a particular purpose, non-infringement, and accuracy.
        </p>
        <p>
          This clause does not exclude any rights you have under the Australian
          Consumer Law or any other non-excludable consumer protection statute.
        </p>
      </>
    ),
  },
  {
    id: "11",
    title: "Limitation of liability",
    body: (
      <>
        <p className="mb-4">
          To the maximum extent permitted by law, DARA&apos;s total aggregate
          liability to you for any and all claims arising out of or related to
          these Terms or the Service is limited to the greater of:
        </p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li>The total amount you have paid us in the twelve (12) months preceding the claim; or</li>
          <li>AUD $1,000.</li>
        </ul>
        <p className="mt-4">
          In no event shall DARA be liable for indirect, incidental, special,
          consequential, or punitive damages — including lost profits, lost
          data, lost goodwill, or business interruption — even if advised of
          the possibility of such damages.
        </p>
      </>
    ),
  },
  {
    id: "12",
    title: "Indemnification",
    body: (
      <>
        You agree to indemnify, defend, and hold harmless DARA, its officers,
        employees, and contractors from any claim, damage, loss, or expense
        (including reasonable legal fees) arising out of (i) your breach of
        these Terms, (ii) your violation of any applicable law, or (iii) your
        Customer Data infringing the rights of a third party.
      </>
    ),
  },
  {
    id: "13",
    title: "Governing law and disputes",
    body: (
      <>
        These Terms are governed by the laws of New South Wales, Australia. Any
        dispute arising out of or in connection with these Terms shall be
        submitted to the exclusive jurisdiction of the courts of New South
        Wales, Australia, and we both agree to that forum. Before commencing
        legal proceedings, the parties will use reasonable endeavours to resolve
        the dispute in good faith.
      </>
    ),
  },
  {
    id: "14",
    title: "Changes to these terms",
    body: (
      <>
        We may update these Terms from time to time. Material changes will be
        notified to active customers by email at least 14 days before they take
        effect. Continued use of the Service after the effective date of a
        change constitutes acceptance of the updated Terms.
      </>
    ),
  },
  {
    id: "15",
    title: "Contact",
    body: (
      <>
        Questions about these Terms should be directed to{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FFB000] underline">{CONTACT_EMAIL}</a>. Support
        enquiries should go to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#FFB000] underline">{SUPPORT_EMAIL}</a>.
      </>
    ),
  },
];

export default function Terms() {
  usePageview("/terms");
  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-black tracking-tight" data-testid="terms-home-link">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            DARA OS
          </Link>
          <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" /> Back home
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
          <FileText className="w-3 h-3" /> LEGAL · TERMS OF SERVICE
        </div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-3">
          Terms of Service
        </h1>
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-12">
          Effective: {EFFECTIVE_DATE} · Operated by {ENTITY}
        </p>

        <div className="space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`terms-section-${s.id}`}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// {s.id}</div>
              <h2 className="font-heading font-black text-2xl tracking-tight mb-4">{s.title}</h2>
              <div className="text-zinc-300 text-base leading-relaxed">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-900 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
          {ENTITY} · {EFFECTIVE_DATE} · v1.0
        </div>
      </main>
    </div>
  );
}
